import { MovementType } from "@/data/movementType";
import { CategoryOption } from "../../a6/A6Transactions/config";

type SignedMovements = { pos: MovementType[]; neg: MovementType[] };
type MovementEntry = MovementType[] | SignedMovements;

const C3_SECURITIES_MOVEMENT_MAP: Record<string, MovementEntry> = {
  private_equity: {
    pos: [
      MovementType.CAPITAL_CALL,
      MovementType.CALL_SUBSEQUENT_CLOSING,
      MovementType.RETURN_EXCESS_DISTRIBUTION,
      MovementType.TRANSFER_FUND_REALLOCATION,
      MovementType.RETURN_EXCESS_TEMPORARY_DISTRIBUTION,
      MovementType.FEEDER_FEE,
    ],
    neg: [
      MovementType.CAPITAL_GAIN,
      MovementType.DISTRIBUTION,
      MovementType.TEMPORARY_RETURN_CAPITAL,
      MovementType.DISTRIBUTION_SUBSEQUENT_CLOSING,
      MovementType.RETURN_EXCESS_CALL,
      MovementType.TRANSFER_FUND_REALLOCATION,
    ],
  },
  real_estate: {
    pos: [],
    neg: [MovementType.ASSET_LIQUIDATION, MovementType.RETURN_EXCESS_CALL],
  },
  private_debt: {
    pos: [MovementType.CAPITAL_CALL],
    neg: [MovementType.ASSET_LIQUIDATION, MovementType.RETURN_EXCESS_CALL],
  },
  // equity, fixed_income, diversified_investments: no category (field hidden)
};

const C3_CASH_MOVEMENT_MAP: Record<string, MovementType[]> = {
  cash: [MovementType.DIVIDEND],
  equity: [MovementType.DIVIDEND],
  diversified_investments: [MovementType.DIVIDEND],
  real_estate: [MovementType.DIVIDEND],
  private_equity: [MovementType.DIVIDEND],
  fixed_income: [MovementType.ACCRUED_INTEREST],
  private_debt: [MovementType.ACCRUED_INTEREST],
};

export function isCashFormat(staticDatumName: string | undefined): boolean {
  if (!staticDatumName) return false;
  return staticDatumName.startsWith("CASH");
}

// Fiduciary/CAT placements share asset_class_2 "cash" with pure cash, so the
// name prefix still distinguishes them; every other asset class (monetary
// funds included) is never treated as cash regardless of its name.
export function isCashAssetClass(
  assetClass2: string | null | undefined,
  staticDatumName: string | undefined,
): boolean {
  return assetClass2 === "cash" && isCashFormat(staticDatumName);
}

export function getC3FilteredCategoryOptions(
  options: CategoryOption[],
  accountType: string | undefined,
  assetClass1: string | null,
  isCashAsset: boolean,
  marketValueLocalCcy?: number | null,
): CategoryOption[] {
  if (!options?.length || !assetClass1 || !accountType) return [];

  let allowed: MovementType[];

  if (accountType === "cash") {
    if (isCashAsset) {
      allowed = [MovementType.FEES];
    } else {
      allowed = C3_CASH_MOVEMENT_MAP[assetClass1] ?? [];
    }
  } else if (accountType === "securities") {
    const entry = C3_SECURITIES_MOVEMENT_MAP[assetClass1];
    if (!entry) return [];

    if (Array.isArray(entry)) {
      allowed = entry.slice();
    } else if (marketValueLocalCcy == null) {
      // no amount yet: show merged pos+neg so user can see all options
      allowed = Array.from(new Set([...entry.pos, ...entry.neg]));
    } else {
      const sign = marketValueLocalCcy > 0 ? "pos" : "neg";
      allowed = entry[sign] ?? [];
    }
  } else {
    return [];
  }

  if (!allowed.length) return [];

  const allowSet = new Set(allowed);
  return options
    .map((section) => ({
      ...section,
      fields: section.fields.filter((f) => allowSet.has(f as MovementType)),
    }))
    .filter((section) => section.fields.length > 0);
}
